import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputComponent } from '../../from-input/input.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'add-media',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './add-media.component.html',
  styleUrls: ['./add-media.component.scss'],
})
export class AddMediaComponent implements OnInit, OnDestroy {
  @Output() formSubmitted = new EventEmitter<FormData>();
  @Output() formValidation = new EventEmitter<boolean>();
  @ViewChild('fileInput') fileInput!: ElementRef;

  form!: FormGroup;
  eventTypeOption: any[] = [];

  selectedFiles: File[] = [];
  imagePreviews: { url: string; name: string }[] = [];
  uploadError: string = '';
  formSubmittedAttempt: boolean = false;
  isUploading: boolean = false;

  private dialogConfirmListener?: (event: Event) => void;
  private readonly MAX_FILE_SIZE = 10 * 1024 * 1024;
  private readonly ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AddMediaComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.eventTypeOption = data?.eventTypeOption || [];
  }

  ngOnInit(): void {
    this.initForm();
    this.setupDialogListener();
  }

  ngOnDestroy(): void {
    this.cleanupDialogListener();
  }

  private initForm(): void {
    this.form = this.fb.group({});

    this.form.valueChanges.subscribe(() => {
      this.formValidation.emit(this.isFormValid());
    });
  }

  private setupDialogListener(): void {
    this.dialogConfirmListener = () => this.handleDialogConfirm();
    document.addEventListener('dialogConfirm', this.dialogConfirmListener);
  }

  private cleanupDialogListener(): void {
    if (this.dialogConfirmListener) {
      document.removeEventListener('dialogConfirm', this.dialogConfirmListener);
    }
  }

  get showFileRequiredError(): boolean {
    return this.formSubmittedAttempt && this.selectedFiles.length === 0;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.clearErrors();

      if (input.files.length > 1) {
        this.uploadError = 'Please upload one file at a time.';
        input.value = '';
        return;
      }

      const files = Array.from(input.files);
      this.processFiles(files);
      input.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const uploadArea = event.currentTarget as HTMLElement;
    uploadArea.classList.add('drag-over');
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const uploadArea = event.currentTarget as HTMLElement;
    uploadArea.classList.remove('drag-over');
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const uploadArea = event.currentTarget as HTMLElement;
    uploadArea.classList.remove('drag-over');

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.clearErrors();
      if (event.dataTransfer.files.length > 1) {
        this.uploadError = 'Please upload one file at a time.';
        return;
      }

      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    }
  }

  private clearErrors(): void {
    this.uploadError = '';
    this.formSubmittedAttempt = false;
  }

  private processFiles(files: File[]): void {
    if (this.selectedFiles.length > 0 && files.length > 0) {
      this.uploadError =
        'Please upload one file at a time.';
      return;
    }
    if (files.length > 1) {
      this.uploadError = 'Please upload one file at a time.';
      return;
    }

    const file = files[0];
    const validation = this.validateFile(file);

    if (validation.valid && validation.file) {
      this.selectedFiles = [];
      this.imagePreviews = [];
      this.selectedFiles.push(validation.file);
      this.generatePreview(validation.file);
    } else if (validation.error) {
      this.uploadError = validation.error;
    }
  }

  private validateFile(file: File): {
    valid: boolean;
    file?: File;
    error?: string;
  } {
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: 'Invalid file format. Please upload a JPG or PNG image',
      };
    }

    if (file.size > this.MAX_FILE_SIZE) {
      return {
        valid: false,
        error: 'File size exceeds 10MB. Please upload a smaller image',
      };
    }

    return { valid: true, file };
  }

  private generatePreview(file: File): void {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagePreviews = [
          {
            url: e.target.result,
            name: file.name,
          },
        ];
      };
      reader.readAsDataURL(file);
    }
  }

  removeImage(index: number): void {
    this.selectedFiles = [];
    this.imagePreviews = [];
    this.uploadError = '';
    this.formSubmittedAttempt = false;
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  private prepareFormData(): FormData | null {
    if (this.selectedFiles.length === 0) {
      this.uploadError = 'Please select an image.';
      return null;
    }

    if (this.selectedFiles.length > 1) {
      this.uploadError = 'Please upload one file at a time.';
      return null;
    }

    try {
      const formData = new FormData();
      this.selectedFiles.forEach((file, index) => {
        formData.append('ImageFiles', file);
      });

      return formData;
    } catch (error) {
      console.error('Error preparing FormData:', error);
      this.uploadError = 'Error preparing files for upload.';
      return null;
    }
  }

  handleDialogConfirm(): void {
    this.formSubmittedAttempt = true;

    const formData = this.prepareFormData();

    if (formData) {
      this.isUploading = true;
      this.formSubmitted.emit(formData);
    } else {
      this.isUploading = false;
    }
  }

  onSubmit(): void {
    this.handleDialogConfirm();
  }

  closeDialog(result?: any): void {
    this.dialogRef.close(result);
  }

  isFormValid(): boolean {
    return this.selectedFiles.length === 1;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
